import React, { useEffect, useState } from "react";
import { useT } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Panel, Btn, Field, Input, Select, Textarea, Modal, DataTable, Plus, toast } from "@/pages/app/kit";
import { Printer, Copy } from "lucide-react";

const DEFAULT_BODY = (title) => `# ${title}

## 1. Kontekst / Context
Żywiąca Polska — pilotaż w regionie Lubelskie.

## 2. Cel / Objective
...

## 3. Zakres / Scope
...

## 4. Model operacyjny / Operating model
Technologia porządkuje. Ludzie decydują. Każda transakcja pozostaje identyfikowalna.

## 5. Następne kroki / Next steps
...

_Ten dokument nie stanowi zatwierdzenia prawnego. This proposal does not claim legal approval._`;

const KWITNIE_BODY = `# Polska Kwitnie — Regional Pollinator & Honey Collection Pilot
### Żywiąca Polska · Miód Żywiącej Polski
_Dane demonstracyjne / Demo data — nie stanowią prognozy cen, produkcji ani zakontraktowanego popytu._

## 1. Kontekst / Context
Zapylacze są infrastrukturą rolnictwa. Pszczelarze są producentami żywności. Pochodzenie miodu musi być prawdziwe i identyfikowalne.
Pollinators are agricultural infrastructure. Beekeepers are food producers. Honey origin must be truthful and traceable.

## 2. Cel / Objective
- Zwiększyć dostępność zapylania dla polskich upraw / Increase pollination availability for Polish crops.
- Połączyć rolników z pszczelarzami przy zatwierdzeniu obu stron / Connect farmers with beekeepers under two-sided approval.
- Zbudować identyfikowalny, prawdziwie polski łańcuch miodu / Build a traceable, genuinely Polish honey chain.
- Chronić zapylacze poprzez rejestr planowanych zabiegów / Protect pollinators via a treatment-notice record.

## 3. Filary programu / Program pillars
1. **Usługi zapylania / Pollination services** — dopasowanie oparte na regułach, zatwierdzane przez rolnika i pszczelarza. Rule-based matching, human-approved by both farmer and beekeeper.
2. **Alerty o zabiegach / Treatment alerts** — narzędzie komunikacji, nie porada dot. doboru środków ani zgodności prawnej. A communication record — not pesticide-selection or legal-compliance advice.
3. **Siedliska i pożytki / Habitat & forage** — kwietne miedze, żywopłoty, rośliny okrywowe; poziomy weryfikacji: Planned / Self-reported / Partner reviewed / Verified.
4. **Jakość i pochodzenie / Quality & origin** — rejestracja partii (MZP-), testy laboratoryjne, identyfikowalność. Rozróżniamy zweryfikowane polskie pochodzenie od miodu jedynie pakowanego w Polsce. We distinguish verified Polish origin from honey merely packed in Poland.

## 4. Model operacyjny / Operating model
Rolnik zgłasza zapotrzebowanie na zapylanie → system proponuje pszczelarzy (reguły + wyjaśnienie) → obie strony zatwierdzają → partie miodu rejestrowane i identyfikowalne → rozliczenia pszczelarzy z jawnymi, uzasadnionymi potrąceniami.
Technologia porządkuje. Ludzie decydują. Każda transakcja pozostaje identyfikowalna.

## 5. Model przychodów (poglądowy) / Revenue model (illustrative)
Opłata koordynacyjna za usługę zapylania, usługa punktu skupu i testowania miodu, marża instytucjonalna na miodzie, identyfikowalność i raportowanie. Wszystkie założenia poglądowe do czasu walidacji. All assumptions illustrative until validated.

## 6. Pomiar wpływu / Impact measurement
Liczba pszczelarzy i rodzin, potwierdzone dopasowania, kg miodu (prognoza / przebadane / zweryfikowane polskie pochodzenie), działania siedliskowe. Metryki oznaczone: Demo / Estimated / Reported / Verified.

## 7. Bezpieczeństwo i zgodność / Safety & compliance
- Miodu nie wolno podawać dzieciom poniżej 12. miesiąca życia. / Honey must not be given to children under 12 months.
- Nie ujawniamy publicznie dokładnych współrzędnych pasiek. / Exact apiary coordinates are not exposed publicly.
- Przegląd w aplikacji nie jest certyfikatem laboratoryjnym. / In-app review is not a laboratory certification.

## 8. Następne kroki / Next steps
Mapowanie partnerów, rekrutacja pszczelarzy, uruchomienie regionalnego punktu skupu, pilotaż sezonu kwitnienia w regionie Lubelskie i wschodnie Mazowieckie.

_Ten dokument nie stanowi zatwierdzenia prawnego. This proposal does not claim legal approval._`;

export default function ProposalsPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const { user } = useAuth();
  const canEdit = ["coordinator", "admin", "super_admin"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/proposals").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (canEdit) api.get("/admin/proposal-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const startNew = (tpl) => setEdit({ template_key: tpl.key, title: tpl.title, body: tpl.key === "polska_kwitnie" ? KWITNIE_BODY : DEFAULT_BODY(tpl.title), region: "lubelskie" });

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (edit.id) await api.put(`/admin/proposals/${edit.id}`, { template_key: edit.template_key, title: edit.title, body: edit.body, region: edit.region });
      else await api.post("/admin/proposals", { template_key: edit.template_key, title: edit.title, body: edit.body, region: edit.region });
      toast.success("OK"); setEdit(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const printProposal = (p) => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${p.title}</title><style>body{font-family:Georgia,serif;max-width:720px;margin:40px auto;line-height:1.6;padding:0 20px}h1{font-size:24px}pre{white-space:pre-wrap;font-family:inherit}</style></head><body><pre>${p.body.replace(/</g,"&lt;")}</pre></body></html>`);
    w.document.close(); w.print();
  };
  const copyProposal = (p) => { navigator.clipboard.writeText(p.body); toast.success(L("Skopiowano","Copied")); };

  const cols = [
    { key: "title", label: L("Tytuł","Title"), render: (r) => <span className="font-semibold">{r.title}</span> },
    { key: "template_key", label: "Template" },
    { key: "created_by_name", label: L("Autor","Author") },
    { key: "act", label: "", render: (r) => (
      <div className="flex gap-2">
        {canEdit && <button className="text-xs font-semibold text-forest hover:underline" onClick={() => setEdit(r)} data-testid={`edit-proposal-${r.id}`}>Edit</button>}
        <button className="text-xs text-soil hover:underline" onClick={() => printProposal(r)} data-testid={`print-proposal-${r.id}`}><Printer className="inline h-3 w-3" /> Print</button>
        <button className="text-xs text-soil hover:underline" onClick={() => copyProposal(r)}><Copy className="inline h-3 w-3" /> Copy</button>
      </div>
    )},
  ];

  return (
    <div>
      <PageTitle title={t("dashboard.proposals")} subtitle={L("Edytowalne szablony propozycji","Editable proposal templates")} />
      {canEdit && (
        <Panel title={L("Nowa propozycja z szablonu","New from template")} className="mb-6">
          <div className="flex flex-wrap gap-2">
            {templates.map((tpl) => (
              <Btn key={tpl.key} variant="outline" icon={Plus} onClick={() => startNew(tpl)} data-testid={`tpl-${tpl.key}`}>{tpl.title}</Btn>
            ))}
          </div>
        </Panel>
      )}
      <DataTable columns={cols} rows={rows} empty={t("empty")} testid="proposals-table" />

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.title || ""} wide>
        {edit && (
          <form onSubmit={save} className="space-y-4">
            <Field label={L("Tytuł","Title")} required><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="proposal-title" /></Field>
            <Field label={L("Treść (Markdown)","Body (Markdown)")}><Textarea rows={14} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className="font-mono text-xs" data-testid="proposal-body" /></Field>
            <Btn type="submit" disabled={busy} data-testid="proposal-save">{busy?"…":t("cta.save")}</Btn>
          </form>
        )}
      </Modal>
    </div>
  );
}
